import React, { useState, useCallback } from "react";
import {
  ActivityIndicator,
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";
import color from "@constants/color";
import Text from "@components/CustomText";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import toast from "@utils/toast";
import {
  pickImageFromCamera,
  pickImageWithChoice,
  recordVideoFromCamera,
} from "./imagePicker";
import { commonStyles, vehicleStyles as styles } from "./styles";
import { useOnboarding } from "./OnboardingContext";
import { useFlowNavigation } from "./useFlowNavigation";
import { DEFAULT_FLOW_CONFIG } from "./FlowConfigContext";
import {
  uploadOnboardingImage,
  submitKycFromContext,
} from "@services/onboardingApi";
import type { StackNavigationProp } from "@react-navigation/stack";
import type { OnboardingStackParamList, DocumentUpload } from "./types";
import type { DocumentItemConfig, DocumentId } from "./flowConfig.types";

interface DocumentsProps {
  navigation: StackNavigationProp<OnboardingStackParamList, "Documents">;
}

interface DocumentsForm {
  aadhaarCard: DocumentUpload;
  panCard: DocumentUpload;
  drivingLicense: DocumentUpload;
  photo: string | null;
  lagganVideo: string | null;
}

const INITIAL_FORM: DocumentsForm = {
  aadhaarCard: {},
  panCard: {},
  drivingLicense: {},
  photo: null,
  lagganVideo: null,
};

type UploadKey =
  | "aadhaarCard:front"
  | "aadhaarCard:back"
  | "panCard:front"
  | "panCard:back"
  | "drivingLicense:front"
  | "drivingLicense:back"
  | "photo"
  | "lagganVideo";

const FALLBACK_DOCUMENTS: DocumentItemConfig[] = (
  DEFAULT_FLOW_CONFIG.db_types[0].steps.find((s) => s.id === "Documents")
    ?.config as { documents: DocumentItemConfig[] } | undefined
)?.documents ?? [
  { id: "aadhaarCard", label: "Aadhaar Card", required: true },
  { id: "panCard", label: "Pan Card", required: true },
  { id: "drivingLicense", label: "Driving License", required: false },
];

const Documents: React.FC<DocumentsProps> = ({ navigation }) => {
  const { data: onboardingData, updateData } = useOnboarding();
  const {
    isLagganFlow,
    goToNextStep,
    getStepConfig,
    getDocumentsConfig,
    willDownstreamStepSubmitKyc,
  } = useFlowNavigation();
  const [form, setForm] = useState<DocumentsForm>(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingKeys, setUploadingKeys] = useState<Set<UploadKey>>(new Set());
  const [localUris, setLocalUris] = useState<
    Partial<Record<UploadKey, string>>
  >({});

  const isLaggan = isLagganFlow();
  const phoneNumber = onboardingData.phoneNumber;

  const stepCfg = getStepConfig<"Documents">();
  const documentConfigs: DocumentItemConfig[] =
    stepCfg?.documents ?? FALLBACK_DOCUMENTS;
  const requiredDocIds = new Set<DocumentId>(
    documentConfigs.filter((d) => d.required).map((d) => d.id),
  );

  const isFormValid =
    (!requiredDocIds.has("aadhaarCard") ||
      (!!form.aadhaarCard.front && !!form.aadhaarCard.back)) &&
    (!requiredDocIds.has("panCard") ||
      (!!form.panCard.front && !!form.panCard.back)) &&
    (!requiredDocIds.has("drivingLicense") ||
      (!!form.drivingLicense.front && !!form.drivingLicense.back)) &&
    !!form.photo &&
    (!isLaggan || !!form.lagganVideo);

  const setUploading = useCallback((key: UploadKey, on: boolean) => {
    setUploadingKeys((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const uploadAsset = useCallback(
    async (asset: {
      uri?: string;
      fileName?: string | null;
      type?: string | null;
    }): Promise<string | null> => {
      if (!asset.uri) return null;
      if (!phoneNumber) {
        toast.showLongToast("Phone number missing — restart onboarding.");
        return null;
      }
      try {
        const url = await uploadOnboardingImage(
          {
            uri: asset.uri,
            name: asset.fileName ?? `upload_${Date.now()}`,
            type: asset.type ?? "image/jpeg",
          },
          phoneNumber,
        );
        return url;
      } catch (err) {
        const axiosError = err as {
          response?: { data?: { message?: string } };
        };
        toast.showLongToast(
          axiosError?.response?.data?.message || "Upload failed. Try again.",
        );
        return null;
      }
    },
    [phoneNumber],
  );

  const pickImage = useCallback(
    async (
      documentKey: "aadhaarCard" | "panCard" | "drivingLicense" | "photo",
      side?: "front" | "back",
    ) => {
      try {
        const asset = await pickImageWithChoice();
        if (!asset) return;

        const key: UploadKey =
          documentKey === "photo"
            ? "photo"
            : (`${documentKey}:${side ?? "front"}` as UploadKey);
        if (asset.uri) setLocalUris((prev) => ({ ...prev, [key]: asset.uri }));
        setUploading(key, true);
        const url = await uploadAsset(asset);
        setUploading(key, false);
        if (!url) return;

        if (documentKey === "photo") {
          setForm((prev) => ({ ...prev, photo: url }));
          updateData({ selfieUrl: url, selfieUri: url });
        } else if (side) {
          setForm((prev) => ({
            ...prev,
            [documentKey]: {
              ...prev[documentKey],
              [side]: url,
            },
          }));
          // Mirror into context immediately so it's available for the batch submit.
          if (documentKey === "aadhaarCard") {
            updateData({
              aadhaarPhotos: {
                ...onboardingData.aadhaarPhotos,
                [side]: url,
              },
            });
          } else if (documentKey === "panCard") {
            updateData({
              panPhotos: { ...onboardingData.panPhotos, [side]: url },
            });
          } else {
            updateData({
              drivingLicensePhotos: {
                ...onboardingData.drivingLicensePhotos,
                [side]: url,
              },
            });
          }
        }
      } catch {
        toast.showLongToast("Failed to pick image. Please try again.");
      }
    },
    [
      setUploading,
      uploadAsset,
      updateData,
      onboardingData.aadhaarPhotos,
      onboardingData.panPhotos,
      onboardingData.drivingLicensePhotos,
    ],
  );

  const takePhoto = useCallback(async () => {
    try {
      const asset = await pickImageFromCamera("front");
      if (!asset) return;

      if (asset.uri) setLocalUris((prev) => ({ ...prev, photo: asset.uri }));
      setUploading("photo", true);
      const url = await uploadAsset(asset);
      setUploading("photo", false);
      if (!url) return;

      setForm((prev) => ({ ...prev, photo: url }));
      updateData({ selfieUrl: url, selfieUri: url });
    } catch {
      toast.showLongToast("Failed to open camera. Please try again.");
    }
  }, [setUploading, uploadAsset, updateData]);

  const takeVideo = useCallback(async () => {
    try {
      const asset = await recordVideoFromCamera();
      if (!asset) return;

      setUploading("lagganVideo", true);
      const url = await uploadAsset(asset);
      setUploading("lagganVideo", false);
      if (!url) return;

      setForm((prev) => ({ ...prev, lagganVideo: url }));
      updateData({ lagganVideoUrl: url, lagganVideoUri: url });
    } catch {
      toast.showLongToast("Failed to record video. Please try again.");
    }
  }, [setUploading, uploadAsset, updateData]);

  const onContinue = useCallback(async () => {
    if (isSubmitting || !isFormValid) return;

    // Doc/selfie URLs are already mirrored into context via upload-on-pick.
    // If a later step (DocumentOwnership / YourDocuments / AddressDetails) owns
    // the /submit-kyc batch, just advance and let it submit (V1, and V2 LAGGAN).
    // Otherwise this is the terminal collection step (V2 NORMAL, where those
    // screens are dropped) and must fire /submit-kyc itself — else the KYC row
    // is never populated and the DB is stranded at status=CREATED.
    if (willDownstreamStepSubmitKyc()) {
      setIsSubmitting(true);
      try {
        goToNextStep();
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    setIsSubmitting(true);
    try {
      await submitKycFromContext(onboardingData, getDocumentsConfig());
      goToNextStep();
    } catch (err: unknown) {
      const msg = (err as Error)?.message || "";
      if (msg.startsWith("MISSING:")) {
        toast.showLongToast(`Missing ${msg.replace("MISSING:", "")}`);
      } else {
        const axiosErr = err as { response?: { data?: { message?: string } } };
        toast.showLongToast(
          axiosErr?.response?.data?.message ||
            "Failed to submit KYC. Please try again.",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    isFormValid,
    goToNextStep,
    willDownstreamStepSubmitKyc,
    onboardingData,
    getDocumentsConfig,
  ]);

  const renderUploadBox = (
    documentKey: "aadhaarCard" | "panCard" | "drivingLicense",
    side: "front" | "back",
  ) => {
    const value = form[documentKey][side];
    const label = side === "front" ? "Upload Front" : "Upload Back";
    const isUploaded = !!value;
    const uploadKey = `${documentKey}:${side}` as UploadKey;
    const isUploading = uploadingKeys.has(uploadKey);

    const localUri = localUris[uploadKey];

    return (
      <Pressable
        style={[styles.uploadBox, isUploaded && styles.uploadBoxFilled]}
        onPress={() => !isUploading && pickImage(documentKey, side)}
        accessibilityRole="button"
        accessibilityLabel={`${label} for ${documentKey}`}
      >
        {isUploading ? (
          <ActivityIndicator color={color.primary} />
        ) : isUploaded && localUri ? (
          <>
            <Image
              source={{ uri: localUri }}
              style={docStyles.thumbnail}
              resizeMode="cover"
            />
            <View style={docStyles.thumbnailBadge}>
              <Text style={docStyles.thumbnailBadgeText}>✓</Text>
            </View>
          </>
        ) : isUploaded ? (
          <>
            <Text style={styles.uploadCheckmark}>✓</Text>
            <Text style={styles.uploadedFileName} numberOfLines={2}>
              Uploaded
            </Text>
          </>
        ) : (
          <>
            <Text style={styles.uploadBoxIcon}>↑</Text>
            <Text style={styles.uploadText}>{label}</Text>
          </>
        )}
      </Pressable>
    );
  };

  const renderDocumentSection = (documentKey: DocumentId, label: string) => {
    const isRequired = requiredDocIds.has(documentKey);
    return (
      <View key={documentKey} style={styles.documentSection}>
        <Text style={styles.documentLabel}>
          {label}
          {isRequired && <Text style={styles.requiredStar}>*</Text>}
        </Text>
        <View style={styles.uploadRow}>
          {renderUploadBox(documentKey, "front")}
          {renderUploadBox(documentKey, "back")}
        </View>
      </View>
    );
  };

  return (
    <>
      <HeaderWithBackAndTitle
        headertitle="Documents"
        navigation={navigation}
        hideShadow
      />

      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <Text variant="title_16" style={styles.sectionTitle}>
          Upload Document
        </Text>

        {renderDocumentSection("aadhaarCard", "Aadhaar Card")}
        {renderDocumentSection("panCard", "Pan Card")}
        {renderDocumentSection("drivingLicense", "Driving License")}

        {/* Photo Upload */}
        <View style={styles.documentSection}>
          <Text style={styles.documentLabel}>
            Upload Photo<Text style={styles.requiredStar}>*</Text>
          </Text>
          <Pressable
            style={[
              styles.photoUploadBox,
              form.photo && styles.uploadBoxFilled,
            ]}
            onPress={takePhoto}
            accessibilityRole="button"
            accessibilityLabel="Take a photo"
          >
            {form.photo && localUris.photo ? (
              <>
                <Image
                  source={{ uri: localUris.photo }}
                  style={docStyles.thumbnail}
                  resizeMode="cover"
                />
                <View style={docStyles.thumbnailBadge}>
                  <Text style={docStyles.thumbnailBadgeText}>✓</Text>
                </View>
              </>
            ) : form.photo ? (
              <>
                <Text style={styles.uploadCheckmark}>✓</Text>
                <Text style={styles.uploadedFileName}>Photo uploaded</Text>
              </>
            ) : (
              <>
                <Text style={styles.uploadEmoji}>📷</Text>
                <Text style={styles.uploadText}>Take Photo</Text>
              </>
            )}
          </Pressable>
        </View>

        {/* Laggan: Short Video Near Home */}
        {isLaggan && (
          <View style={styles.documentSection}>
            <Text style={styles.documentLabel}>
              Short Video Near Home<Text style={styles.requiredStar}>*</Text>
            </Text>
            <Pressable
              style={[
                styles.photoUploadBox,
                form.lagganVideo && styles.uploadBoxFilled,
              ]}
              onPress={takeVideo}
              accessibilityRole="button"
              accessibilityLabel="Record a short video near your home"
            >
              {form.lagganVideo ? (
                <>
                  <Text style={styles.uploadCheckmark}>✓</Text>
                  <Text style={styles.uploadedFileName}>Video uploaded</Text>
                </>
              ) : (
                <>
                  <Text style={styles.uploadEmoji}>🎥</Text>
                  <Text style={styles.uploadText}>Take Video</Text>
                </>
              )}
            </Pressable>
          </View>
        )}
      </ScrollView>

      <View style={styles.bottomBar}>
        <Pressable
          style={[
            commonStyles.primaryButton,
            !isFormValid && commonStyles.primaryButtonDisabled,
          ]}
          disabled={isSubmitting || !isFormValid}
          onPress={onContinue}
          accessibilityRole="button"
          accessibilityLabel="Continue"
        >
          <Text variant="title_16" style={commonStyles.primaryButtonText}>
            Continue
          </Text>
        </Pressable>
      </View>
    </>
  );
};

const docStyles = StyleSheet.create({
  thumbnail: {
    ...StyleSheet.absoluteFill,
    borderRadius: 7,
  },
  thumbnailBadge: {
    position: "absolute",
    top: 6,
    right: 6,
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: color.plain_green,
    alignItems: "center",
    justifyContent: "center",
  },
  thumbnailBadgeText: {
    color: color.white,
    fontSize: 11,
    lineHeight: 13,
  },
});

export default Documents;
